import type { FC } from 'react';
import { Suspense, lazy, useEffect } from 'react';

import type { MainPath, MainRoute } from '@snowit/wouter';

import useAuth from '@snowit/system/useAuth';
import useCategories from '@snowit/system/useCategories';
import useDistricts from '@snowit/system/useDistricts/useDistricts';
import usePartners from '@snowit/system/usePartners';
import useTenants from '@snowit/system/useTenants';
import useVenues from '@snowit/system/useVenues';
import useZones from '@snowit/system/useZones';
import { NotFound, Route, Switch, allPaths, useRouter } from '@snowit/wouter';
import '@snowit/style';

import Layout from '@snowit/components/Layout';
import { useStore } from '@snowit/store';
import useDepartures from '@snowit/system/useDepartures';
import useOptions from '@snowit/system/useOptions';
import usePolicies from '@snowit/system/usePolicies';
import useSeasons from '@snowit/system/useSeasons';
import useShippingMethods from '@snowit/system/useShippingMethods';
import useTags from '@snowit/system/useTags';

import Login from './components/Login';
import WidgetPreview from './components/WidgetPreview';
import ErrorBoundary from './ErrorBoundary';
import useGlobals from './globals';
import Helmet from './Helmet';
import Template from './Templates';

const AdminTools = lazy(() => import('./viewsV2/AdminTools'));
const SignInAsModal = lazy(() => import('./components/SignInAsModal'));
const ApiKeys = lazy(() => import('./viewsV2/ApiKeys'));
const Availability = lazy(() => import('./viewsV2/Availability'));
const Carousels = lazy(() => import('./viewsV2/Carousels'));
const Cart = lazy(() => import('./viewsV2/Cart'));
const Cassa = lazy(() => import('./viewsV2/Cassa'));
const Categories = lazy(() => import('./viewsV2/Categories'));
const Challenges = lazy(() => import('./viewsV2/Challenges'));
const Changelogs = lazy(() => import('./viewsV2/Changelogs'));
const CheckoutCodes = lazy(() => import('./viewsV2/CheckoutCodes'));
const Coupons = lazy(() => import('./viewsV2/Coupons'));
const DealerOrders = lazy(() => import('./viewsV2/DealerOrders'));
const Departures = lazy(() => import('./viewsV2/Departures'));
const Discounts = lazy(() => import('./viewsV2/Discounts'));
const Districts = lazy(() => import('./viewsV2/Districts'));
const EmailsPage = lazy(() => import('./views/Settings/EmailsPage'));
const ExternalCodes = lazy(() => import('./viewsV2/ExternalCodes'));
const FileDrive = lazy(() => import('./viewsV2/FileDrive'));
const Galleries = lazy(() => import('./viewsV2/Galleries'));
const GraphPartner = lazy(() => import('./views/Graphs/GraphPartner'));
const Graphs = lazy(() => import('./views/Graphs'));
const HomePage = lazy(() => import('./views/Home/HomePage'));
const ImportExport = lazy(() => import('./viewsV2/ImportExport'));
const Memberships = lazy(() => import('./viewsV2/Memberships'));
const MembershipsStats = lazy(() => import('./viewsV2/MembershipsStats'));
const Monitoring = lazy(() => import('./viewsV2/Monitoring'));
const MyPartner = lazy(() => import('./viewsV2/MyPartner'));
const Notifications = lazy(() => import('./viewsV2/Notifications'));
const Orders = lazy(() => import('./viewsV2/Orders'));
const Partners = lazy(() => import('./viewsV2/Partners'));
const PermissionsRoot = lazy(() => import('./viewsV2/Permissions'));
const Policies = lazy(() => import('./viewsV2/Policies'));
const Prices = lazy(() => import('./viewsV2/Prices'));
const ProductOptions = lazy(() => import('./viewsV2/ProductOptions'));
const Products = lazy(() => import('./viewsV2/Products'));
const Profiles = lazy(() => import('./viewsV2/Profiles'));
const ProviderOrders = lazy(() => import('./viewsV2/ProviderOrders'));
const Quickbooks = lazy(() => import('./viewsV2/Quickbooks'));
const QuickSearch = lazy(() => import('./viewsV2/QuickSearchOrders'));
const QuickSearchCard = lazy(() => import('./viewsV2/QuickSearchCard'));
const RentalOrder = lazy(() => import('./pdfs/RentalOrder'));
const Reviews = lazy(() => import('./viewsV2/Reviews'));
const RolesRoot = lazy(() => import('./viewsV2/Roles'));
const Routes = lazy(() => import('./viewsV2/Routes'));
const Seasons = lazy(() => import('./viewsV2/Seasons'));
const Settings = lazy(() => import('./viewsV2/Settings'));
const ShippingMethods = lazy(() => import('./viewsV2/ShippingMethods'));
const SkipassGroups = lazy(() => import('./viewsV2/SkipassGroups'));
const SkipassPromos = lazy(() => import('./viewsV2/SkipassPromos'));
const Snowitcards = lazy(() => import('./viewsV2/Snowitcards'));
const Stages = lazy(() => import('./viewsV2/Stages'));
const Tags = lazy(() => import('./viewsV2/Tags'));
const TariffarioPPU = lazy(() => import('./viewsV2/TariffarioPPU'));
const TaskAI = lazy(() => import('./viewsV2/TaskAI'));
const Transits = lazy(() => import('./viewsV2/Transits'));
const UserEmails = lazy(() => import('./viewsV2/UserEmails'));
const UserCreate = lazy(() => import('./viewsV2/UserCreate'));
const Users = lazy(() => import('./viewsV2/Users'));
const Venues = lazy(() => import('./viewsV2/Venues'));
const Webshops = lazy(() => import('./viewsV2/Webshops'));
const Zones = lazy(() => import('./viewsV2/Zones'));
const Turnstiles = lazy(() => import('./viewsV2/Turnstiles'));
const SogliePPU = lazy(() => import('./viewsV2/SogliePPU'));
const QuickSearchMembership = lazy(() => import('./viewsV2/QuickSearchMembership'));

const PathComponents: Record<MainRoute, FC> = {
  apikeys: ApiKeys,
  cart: Cart,
  cassaskipass: Cassa,
  categories: Categories,
  challenges: Challenges,
  checkout_codes: CheckoutCodes,
  coupons_hotel: SkipassPromos,
  coupons: Coupons,
  dealers: DealerOrders,
  departures: Departures,
  discounts: Discounts,
  districts: Districts,
  external_codes: ExternalCodes,
  file_drive: FileDrive,
  memberships_stats: MembershipsStats,
  memberships: Memberships,
  monitoring: Monitoring,
  my_partner: MyPartner,
  notifications: Notifications,
  orders: Orders,
  partners: Partners,
  policies: Policies,
  prices: Prices,
  product_options: ProductOptions,
  products: Products,
  profiles: Profiles,
  providers: ProviderOrders,
  quick_search_card: QuickSearchCard,
  quick_search_membership: QuickSearchMembership,
  quickbooks: Quickbooks,
  reviews: Reviews,
  seasons: Seasons,
  settings: Settings,
  shipping_methods: ShippingMethods,
  skipass_groups: SkipassGroups,
  snowitcards: Snowitcards,
  tariffario_ppu: TariffarioPPU,
  task_ai: TaskAI,
  check_soglie_ppu: SogliePPU,
  user_emails: UserEmails,
  venues: Venues,
  webshop: Webshops,
  zones: Zones,
  // TO REFACTOR
  admin_tools: AdminTools,
  availability: Availability,
  carousels: Carousels,
  changelogs: Changelogs,
  emails: EmailsPage,
  galleries: Galleries,
  graphs: Graphs,
  import_export: ImportExport,
  looker: GraphPartner,
  pdf: RentalOrder,
  permissions: PermissionsRoot,
  quick_search: QuickSearch,
  roles: RolesRoot,
  routes: Routes,
  stages: Stages,
  tags: Tags,
  users: Users,
  user_create: UserCreate,
  transits: Transits,
  turnstiles: Turnstiles,
  '/': HomePage,
};

const App = () => {
  const { logged, checked } = useAuth();
  const { getTenants } = useTenants();
  const { getPartners } = usePartners();
  const { getZones } = useZones();
  const { getDistricts } = useDistricts();
  const { getCategories } = useCategories();
  const { getVenues } = useVenues();
  const { getOptions } = useOptions();
  const { getSeasons } = useSeasons();
  const { getTags } = useTags();
  const { getDepartures } = useDepartures();
  const { location } = useRouter();
  const { store, setStore } = useStore();
  const tenantsReady = store.tenants.ready;
  const { getPolicies } = usePolicies();
  const { getShippingMethods } = useShippingMethods();
  const [, mainRoute] = location.split('/');
  const globals = useGlobals();

  (window as any).snowit = globals;

  useEffect(() => {
    setStore('brevo', { list: [] });
  }, [mainRoute]);

  useEffect(() => {
    if (!tenantsReady) {
      getTenants();
    }
  }, [tenantsReady]);

  useEffect(() => {
    if (logged) {
      getPartners();
      getZones();
      getDepartures();
      getVenues();
      getDistricts();
      getCategories();
      getShippingMethods();
      getOptions();
      getPolicies();
      getSeasons();
      getTags();
    }
  }, [logged]);

  if (!checked) return <Template />;

  const renderRoutes = () => {
    return (
      <Switch>
        {Object.entries<MainRoute, MainPath>(allPaths).map(([key, path]) => {
          return <Route key={key} path={path} component={PathComponents[key]} />;
        })}
        <Route component={NotFound}>{'not-found'}</Route>
      </Switch>
    );
  };

  const renderComponent = () => {
    if (!logged) {
      return <Login />;
    }
    if (window.location.pathname.startsWith('/__ews__')) {
      return <WidgetPreview />;
    }
    return <Layout>{renderRoutes()}</Layout>;
  };

  return (
    <Suspense fallback={<Template />}>
      <ErrorBoundary>
        <Helmet />
        {renderComponent()}
        <SignInAsModal isMounted={Boolean(store.users.selected)} userUID={store.users.selected} />
      </ErrorBoundary>
    </Suspense>
  );
};

export default App;
